import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import LoadingWidgets from '@/components/UI/loading/LoadingWidgets';
import { widgetsIcon } from '@/components/Widgets/constants';
import { getAccountList } from '../../../api/accounts';
import { useClientAccountWidgetList } from '../../../hooks/ReactQueryHooks/useDashboardQuery';
import type { ClientListInterface } from '../../SageAdmin/Clients/types';
import type { WidgetInterface, WidgetsGroupInterface, WidgetsNavItemInterface } from './types';

import WidgetsList from './WidgetsList';

function WidgetsManagement() {
  const [activeNavItem, setActiveNavItem] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<ClientListInterface>();

  const currentSubdomain = window.location.hostname.split('.')[0];

  //Tanstack: version 5 query implementation
  const fetchClientList = useQuery({
    queryKey: ['get-client-account-list'],
    queryFn: () => getAccountList({}),
    select: (data: any) => {
      const transformedClientList = data?.data?.filter((c: ClientListInterface) => c.subdomain !== currentSubdomain);
      return transformedClientList;
    },
    keepPreviousData: true,
    staleTime: 5000,
  });
  const clientList = fetchClientList?.data;

  const clientWidget = useClientAccountWidgetList({
    accountId: selectedClient?.id,
  });

  const widgets = clientWidget?.data?.data?.map((wdg: WidgetsGroupInterface) => {
    const transformedWidgets = wdg?.widgets?.map((twdg: WidgetInterface) => ({
      ...twdg,
      icon: widgetsIcon[twdg.slug],
    }));
    return { ...wdg, widgets: transformedWidgets };
  });

  const widgetsNavItems: WidgetsNavItemInterface[] = [
    { slug: 'USER_DASHBOARD', name: 'User dashboard' },
    { slug: 'COMPANY_DASHBOARD', name: 'Company dashboard' },
    { slug: 'SHERPA_DASHBOARD', name: 'Sherpa dashboard' },
    { slug: 'PROJECT_DASHBOARD', name: 'Project dashboard' },
  ];

  const clientChangeHandler = (option: ClientListInterface) => {
    setActiveNavItem('USER_DASHBOARD');
    setSelectedClient(option);
  };

  return (
    <div className='p-8'>
      <div className='flex justify-between gap-8 border-b-2 border-gray-200 pb-4'>
        <div>
          <h2 className='m-0'>Client Assigned Widgets</h2>
          <small className='text-xs text-gray-500'>List of widgets assigned to clients</small>
        </div>
        <div className='w-1/3'>
          <ReactSelect
            id='selectClientAccount'
            options={clientList}
            placeholder='Select client'
            getOptionLabel={(option) => (option as ClientListInterface).name}
            getOptionValue={(option) => (option as ClientListInterface).id.toString()}
            onChange={(option) => clientChangeHandler(option as ClientListInterface)}
          />
        </div>
      </div>
      {selectedClient && (
        <div className='flex gap-8 pt-5 mb-3 border-b-2'>
          {widgetsNavItems.map((item: WidgetsNavItemInterface) => (
            <h4
              key={item.slug}
              className={`hover:text-primaryColor-normal cursor-pointer border-b-2 pb-3 hover:border-primaryColor-normal ${
                activeNavItem === item.slug && 'border-primaryColor-normal text-primaryColor-normal'
              }`}
              onClick={() => setActiveNavItem(item.slug)}
            >
              {item.name}
            </h4>
          ))}
        </div>
      )}
      {selectedClient && (
        <section className='mt-5 space-y-5'>
          {widgets?.map((wdg: WidgetsGroupInterface) => {
            if (activeNavItem === wdg.slug) {
              return <WidgetsList widgets={wdg.widgets} accountId={selectedClient.id} />;
            }
            return <></>;
          })}
        </section>
      )}
      {!selectedClient && <div className='py-5'>Please select client from dropdown list</div>}
      {selectedClient && clientWidget.isLoading && <LoadingWidgets />}
    </div>
  );
}

export default WidgetsManagement;
